import React, { useState, useEffect } from 'react';
import '@/App.css';
import { motion } from 'framer-motion';
import { 
  DataScienceFinanceDashboard 
} from '@/components/courses/DataScienceFinance';
import {
  InvestorsAcademyDashboard
} from '@/components/courses/InvestorsAcademy';

/**
 * StudyCoach Academy - Main Landing Page
 * With Investor's Academy and Data Science Finance courses integrated
 */

// Animation variants for fade-up effect
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Course data - including new Data Science Finance course
const COURSES = [
  {
    id: 'human-medicine',
    icon: '🏥',
    iconBg: 'rgba(231,76,60,.12)',
    name: 'Foundations of Human Medicine',
    desc: 'From cell biology to diagnostics — how the body works, what goes wrong, and how we fix it.',
    tags: ['15 chapters', 'Beginner → Advanced']
  },
  {
    id: 'functional-medicine',
    icon: '🌿',
    iconBg: 'rgba(39,174,96,.12)',
    name: 'Functional Medicine & Longevity',
    desc: 'NAD+, gut microbiome, epigenetics, sleep science — optimising healthspan with evidence.',
    tags: ['15 chapters', 'Intermediate']
  },
  {
    id: 'supplements',
    icon: '💊',
    iconBg: 'rgba(142,68,173,.12)',
    name: 'Supplements Deep Dive',
    desc: 'NMN, Methylcobalamin B12, Omega-3, Magnesium — what the science actually shows.',
    tags: ['15 chapters', 'Evidence-based']
  },
  {
    id: 'emergency-medicine',
    icon: '🚨',
    iconBg: 'rgba(230,126,34,.12)',
    name: 'Emergency Medicine',
    desc: 'ABCDE approach, CPR, trauma, anaphylaxis, emergency drugs — life-saving knowledge.',
    tags: ['15 chapters', 'Clinical focus']
  },
  {
    id: 'pharmacology',
    icon: '💉',
    iconBg: 'rgba(41,128,185,.12)',
    name: 'Essential Pharmacology',
    desc: 'How every major drug class works — mechanisms, side effects, interactions, clinical use.',
    tags: ['15 chapters', 'Intermediate']
  },
  {
    id: 'investors-academy',
    icon: '📈',
    iconBg: 'rgba(200,169,110,.15)',
    name: "Investor's Academy",
    desc: 'From money basics to launching a hedge fund — 44 deep lessons, real case studies, and an AI tutor built in.',
    tags: ['5 phases', '44 lessons', 'Free'],
    isLive: true
  },
  {
    id: 'data-science-finance',
    icon: '📊',
    iconBg: 'rgba(251,146,60,.15)',
    name: 'Data Science for Finance & FMAA',
    desc: 'From Python basics to Machine Learning — become a Financial Data Analyst with AI tutoring built in.',
    tags: ['8 stages', '50+ topics', 'Free'],
    isLive: true,
    isNew: true
  }
];

const FEATURES = [
  {
    icon: '✨',
    iconBg: 'rgba(200,169,110,.12)',
    title: 'AI Course Designer',
    desc: 'Just tell it what you want to learn. The AI asks a few questions, then builds your complete personalised curriculum — chapters, timeline, learning path, prerequisites. No forms. No friction.',
    tag: 'Conversational'
  },
  {
    icon: '🎓',
    iconBg: 'rgba(124,110,255,.12)',
    title: 'AI Tutor Mid-Quiz',
    desc: "After every question, a full AI tutor appears. Ask why you got it wrong, request a real-world analogy, ask if it's exam-relevant. It knows your course, your chapter, and your exact answer.",
    tag: 'Contextual AI'
  },
  {
    icon: '🧠',
    iconBg: 'rgba(0,212,160,.1)',
    title: 'Spaced Repetition',
    desc: "Built on the SM-2 algorithm used by the world's best learners. Every answer you give updates your personal learning schedule. The system knows exactly when to bring back weak topics.",
    tag: 'Science-Based'
  },
  {
    icon: '🎧',
    iconBg: 'rgba(30,213,84,.1)',
    title: 'Audio Summaries',
    desc: 'AI generates podcast-style audio summaries of any chapter. Listen on your commute, at the gym, while cooking. Adjustable speed from 0.75x to 2x. Words highlighted as you listen.',
    tag: 'Learn Anywhere'
  },
  {
    icon: '📹',
    iconBg: 'rgba(255,0,0,.08)',
    title: 'Curated YouTube',
    desc: "AI finds the best YouTube videos and channels for every chapter you're studying. Not a random search — curated recommendations that actually match your level and learning goal.",
    tag: 'Multi-Modal'
  },
  {
    icon: '📄',
    iconBg: 'rgba(255,77,109,.1)',
    title: 'Upload Your Notes',
    desc: 'Drop a PDF, photo of handwritten notes, or past exam paper. The AI generates questions directly from your material — matching the style of real exam papers when you tell it to.',
    tag: 'Your Material'
  }
];

const STEPS = [
  {
    num: '1',
    title: 'Tell the AI what you want to learn',
    desc: 'No forms, no dropdowns. Just tell it in plain language. It asks a few questions to understand your level and goal.',
    example: '"I want to become a doctor"\n"Teach me emergency pharmacology"\n"I\'m building a rocket — teach me aerospace engineering"'
  },
  {
    num: '2',
    title: 'AI designs your complete curriculum',
    desc: 'In seconds, you get a full course — chapters, timeline, learning path, prerequisites. Personalised to your knowledge level, goals and available time.'
  },
  {
    num: '3',
    title: 'Read, listen or watch — your choice',
    desc: 'Deep AI-written lessons for reading. Podcast-style audio for commuting. Curated YouTube videos for visual learners. All for the same chapter.'
  },
  {
    num: '4',
    title: 'Quiz yourself, then ask the tutor',
    desc: "AI generates exam-quality questions. After every answer, an AI tutor is right there — ask why, ask for examples, ask if it'll be on the exam."
  },
  {
    num: '5',
    title: 'Spaced repetition handles the rest',
    desc: 'The system tracks every question. Weak topics come back at the perfect moment. Strong topics fade into the background. You always study what matters most.'
  }
];

const TESTIMONIALS = [
  {
    text: "I told it I wanted to understand emergency medicine and it built me a 15-chapter course in seconds. I've been learning for 3 weeks and I feel like I understand more than I did after months of reading textbooks.",
    name: 'Sarah M.',
    role: 'Nursing Student',
    avatar: '👩‍⚕️',
    avatarBg: 'rgba(231,76,60,.2)'
  },
  {
    text: "The AI tutor is the thing that gets me. I got a question wrong about beta blockers and just asked 'why is my answer wrong?' — it gave me a clinical analogy I'll never forget. No textbook does that.",
    name: 'James K.',
    role: 'Medical Student',
    avatar: '👨‍🎓',
    avatarBg: 'rgba(41,128,185,.2)'
  },
  {
    text: "I listen to the audio summaries every morning on my commute. By the time I get to work I've revised a full chapter. I've been doing this for a month and my quiz scores have gone from 60% to 85%.",
    name: 'Priya R.',
    role: 'FMAA Candidate',
    avatar: '👩‍💼',
    avatarBg: 'rgba(39,174,96,.2)'
  }
];

// Components
const Navbar = () => (
  <nav className="sc-nav">
    <div className="sc-nav-inner">
      <div className="sc-logo">
        <span className="sc-logo-icon">🎓</span>
        <span className="sc-logo-text">StudyCoach Academy</span>
      </div>
      <button className="sc-nav-btn">Join Waitlist</button>
    </div>
  </nav>
);

const Hero = ({ onScrollToWaitlist }) => {
  const [count, setCount] = useState(247);
  
  useEffect(() => {
    // Simulate counter incrementing occasionally
    const interval = setInterval(() => {
      if (Math.random() > 0.7) {
        setCount(c => c + 1);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="sc-hero">
      {/* Decorative orbs */}
      <div className="sc-orb sc-orb-1"></div>
      <div className="sc-orb sc-orb-2"></div>
      <div className="sc-orb sc-orb-3"></div>
      
      <motion.div 
        className="sc-hero-badge"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        Now accepting early access applications
      </motion.div>

      <motion.h1 
        className="sc-hero-title"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        Your Personal<br/>
        <em>AI University</em>
      </motion.h1>

      <motion.p 
        className="sc-hero-sub"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        Tell the AI what you want to learn.<br/>
        It designs your entire <strong>personalised curriculum</strong>,<br/>
        teaches you, quizzes you, and adapts to you — forever.
      </motion.p>

      <motion.div 
        className="sc-waitlist-form"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <input 
          type="email" 
          className="sc-waitlist-input" 
          placeholder="Enter your email address…"
        />
        <button className="sc-waitlist-btn">Join Waitlist →</button>
      </motion.div>

      <motion.p 
        className="sc-waitlist-note"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        No spam. Unsubscribe anytime. · studycoach.academy · Early access gets lifetime pricing.
      </motion.p>

      <motion.div 
        className="sc-counter-strip"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <div className="sc-counter-item">
          <span className="sc-counter-num">{count}</span>
          <span className="sc-counter-label">People Waiting</span>
        </div>
        <div className="sc-counter-div"></div>
        <div className="sc-counter-item">
          <span className="sc-counter-num">7</span>
          <span className="sc-counter-label">Expert Courses</span>
        </div>
        <div className="sc-counter-div"></div>
        <div className="sc-counter-item">
          <span className="sc-counter-num">90+</span>
          <span className="sc-counter-label">Chapters Ready</span>
        </div>
        <div className="sc-counter-div"></div>
        <div className="sc-counter-item">
          <span className="sc-counter-num">∞</span>
          <span className="sc-counter-label">Questions Available</span>
        </div>
      </motion.div>
    </section>
  );
};

const FeaturesSection = () => (
  <section className="sc-section">
    <div className="sc-container">
      <span className="sc-section-label">What makes us different</span>
      <h2 className="sc-section-title">
        Not a study app.<br/><em>An AI that teaches you.</em>
      </h2>
      <p className="sc-section-sub">
        Every other platform makes you do the work. StudyCoach Academy builds your curriculum, 
        generates your questions, explains your mistakes, and adapts every session based on how you're actually performing.
      </p>

      <motion.div 
        className="sc-features-grid"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        {FEATURES.map((feature, idx) => (
          <motion.div key={idx} className="sc-feature-card" variants={fadeUp}>
            <div className="sc-feature-icon" style={{ background: feature.iconBg }}>
              {feature.icon}
            </div>
            <div className="sc-feature-title">{feature.title}</div>
            <div className="sc-feature-desc">{feature.desc}</div>
            <span className="sc-feature-tag">{feature.tag}</span>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

const CoursesSection = ({ onCourseClick }) => (
  <section className="sc-section sc-courses-section">
    <div className="sc-container">
      <span className="sc-section-label">Ready immediately</span>
      <h2 className="sc-section-title">
        Seven expert courses.<br/><em>Hundreds more via AI.</em>
      </h2>
      <p className="sc-section-sub">
        Our medical & finance library is built and waiting. Add any course and start learning in seconds. 
        Or tell the AI designer what you want — it builds courses on any subject under the sun.
      </p>

      <motion.div 
        className="sc-courses-grid"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        {COURSES.map((course, idx) => (
          <motion.div 
            key={course.id} 
            className={`sc-course-card ${course.isLive ? 'sc-course-live' : ''}`}
            variants={fadeUp}
            onClick={() => course.isLive && onCourseClick(course.id)}
            style={{ cursor: course.isLive ? 'pointer' : 'default' }}
          >
            {course.isNew && (
              <div className="sc-course-new-badge">NEW</div>
            )}
            {course.isLive && (
              <div className="sc-course-live-badge">
                <span className="sc-live-dot"></span>
                <span className="sc-live-dot"></span>
                LIVE NOW
              </div>
            )}
            <div className="sc-course-icon" style={{ background: course.iconBg }}>
              {course.icon}
            </div>
            <div className="sc-course-name">{course.name}</div>
            <div className="sc-course-desc">{course.desc}</div>
            <div className="sc-course-meta">
              {course.tags.map((tag, i) => (
                <span key={i} className={`sc-course-tag ${tag === 'Free' ? 'sc-tag-free' : ''}`}>
                  {tag}
                </span>
              ))}
            </div>
            {course.isLive && (
              <button className="sc-course-start-btn">Start Learning →</button>
            )}
          </motion.div>
        ))}
      </motion.div>

      <div className="sc-courses-note">
        + AI Designer builds custom courses on <em>any subject</em> — medicine, engineering, philosophy, finance, languages, space exploration…
      </div>
    </div>
  </section>
);

const HowItWorksSection = () => (
  <section className="sc-section">
    <div className="sc-container">
      <span className="sc-section-label">The experience</span>
      <h2 className="sc-section-title">
        <em>Learn anything.</em><br/>In five steps.
      </h2>

      <motion.div 
        className="sc-steps"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        {STEPS.map((step, idx) => (
          <motion.div key={idx} className="sc-step" variants={fadeUp}>
            <div className="sc-step-num">{step.num}</div>
            <div className="sc-step-content">
              <div className="sc-step-title">{step.title}</div>
              <div className="sc-step-desc">{step.desc}</div>
              {step.example && (
                <div className="sc-step-example">{step.example}</div>
              )}
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

const PricingSection = () => (
  <section className="sc-section sc-pricing-section">
    <div className="sc-container">
      <span className="sc-section-label">Simple pricing</span>
      <h2 className="sc-section-title">
        Pay once.<br/><em>Learn forever.</em>
      </h2>
      <p className="sc-section-sub">
        Early access members lock in lifetime pricing before we launch publicly.
      </p>

      <motion.div 
        className="sc-pricing-grid"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <motion.div className="sc-price-card" variants={fadeUp}>
          <div className="sc-price-name">Free</div>
          <div className="sc-price-amount">$0</div>
          <div className="sc-price-period">Forever free</div>
          <ul className="sc-price-features">
            <li>3 courses</li>
            <li>5 questions per day</li>
            <li>AI course designer</li>
            <li>Basic spaced repetition</li>
          </ul>
          <button className="sc-price-btn sc-price-btn-ghost">Get Early Access</button>
        </motion.div>

        <motion.div className="sc-price-card sc-price-featured" variants={fadeUp}>
          <div className="sc-price-badge">Most Popular</div>
          <div className="sc-price-name">Pro</div>
          <div className="sc-price-amount">$9<span>.99</span></div>
          <div className="sc-price-period">per month · cancel anytime</div>
          <ul className="sc-price-features">
            <li>Unlimited courses</li>
            <li>Unlimited questions</li>
            <li>Full AI tutor access</li>
            <li>Audio summaries</li>
            <li>YouTube curation</li>
            <li>Past paper analysis</li>
            <li>Full medical library</li>
          </ul>
          <button className="sc-price-btn sc-price-btn-primary">Join Waitlist → Pro</button>
        </motion.div>

        <motion.div className="sc-price-card" variants={fadeUp}>
          <div className="sc-price-name">Lifetime</div>
          <div className="sc-price-amount">$149</div>
          <div className="sc-price-period">one-time · yours forever</div>
          <ul className="sc-price-features">
            <li>Everything in Pro</li>
            <li>Never pay again</li>
            <li>All future courses included</li>
            <li>Priority support</li>
            <li>Early access to new features</li>
          </ul>
          <button className="sc-price-btn sc-price-btn-ghost">Lock in Lifetime Price</button>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

const TestimonialsSection = () => (
  <section className="sc-section">
    <div className="sc-container">
      <span className="sc-section-label">Early feedback</span>
      <h2 className="sc-section-title">
        What early users<br/><em>are saying.</em>
      </h2>

      <motion.div 
        className="sc-testimonials-grid"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        {TESTIMONIALS.map((t, idx) => (
          <motion.div key={idx} className="sc-testimonial" variants={fadeUp}>
            <div className="sc-testimonial-text">"{t.text}"</div>
            <div className="sc-testimonial-author">
              <div className="sc-testimonial-avatar" style={{ background: t.avatarBg }}>
                {t.avatar}
              </div>
              <div>
                <div className="sc-testimonial-name">{t.name}</div>
                <div className="sc-testimonial-role">{t.role}</div>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

const FinalCTA = () => (
  <section className="sc-final-cta">
    <div className="sc-container">
      <div className="sc-divider"></div>
      <h2>
        <em>Start learning</em><br/>anything. Today.
      </h2>
      <p>Join the waitlist. Get early access. Lock in lifetime pricing before we launch publicly.</p>
      <div className="sc-waitlist-form" style={{ maxWidth: 480, margin: '0 auto 16px' }}>
        <input 
          type="email" 
          className="sc-waitlist-input" 
          placeholder="Your email address…"
        />
        <button className="sc-waitlist-btn">Join Waitlist</button>
      </div>
      <p className="sc-waitlist-note">
        Free forever plan available. No credit card required. · studycoach.academy
      </p>
    </div>
  </section>
);

const Footer = () => (
  <footer className="sc-footer" role="contentinfo">
    <div className="sc-container">
      <div className="sc-footer-inner">
        <div className="sc-footer-logo">
          <span aria-hidden="true">🎓</span> StudyCoach Academy
        </div>
        <div className="sc-footer-links">
          <a href="#privacy" aria-label="Privacy Policy">Privacy</a>
          <a href="#terms" aria-label="Terms of Service">Terms</a>
          <a href="#contact" aria-label="Contact Us">Contact</a>
        </div>
        <div className="sc-footer-copy">
          © 2026 HikariGrove Inc. All rights reserved.
        </div>
      </div>
    </div>
  </footer>
);

// Main App
function App() {
  const [currentView, setCurrentView] = useState('landing');

  // Dynamic page title for SEO
  useEffect(() => {
    const titles = {
      'landing': 'StudyCoach Academy — AI-Powered Finance & Data Science Courses',
      'data-science-finance': 'Data Science for Finance — Python, ML & Portfolio Construction | StudyCoach Academy',
      'investors-academy': "Investor's Academy — From Basics to Hedge Fund Management | StudyCoach Academy"
    };
    document.title = titles[currentView] || titles['landing'];
  }, [currentView]);

  // Show Investor's Academy Dashboard
  if (currentView === 'investors-academy') {
    return (
      <InvestorsAcademyDashboard 
        onBack={() => setCurrentView('landing')} 
      />
    );
  }

  // Show Data Science Finance Dashboard
  if (currentView === 'data-science-finance') {
    return (
      <DataScienceFinanceDashboard 
        onBack={() => setCurrentView('landing')} 
      />
    );
  }

  // Landing Page
  return (
    <div className="sc-app">
      <Navbar />
      <main>
      <Hero />
      <FeaturesSection />
      <CoursesSection onCourseClick={(id) => {
        if (id === 'data-science-finance') {
          setCurrentView('data-science-finance');
        } else if (id === 'investors-academy') {
          setCurrentView('investors-academy');
        }
      }} />
      <HowItWorksSection />
      <PricingSection />
      <TestimonialsSection />
      <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}

export default App;
